import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faSave, faTimes, faLayerGroup, faStethoscope, faBowlFood, faWheelchair, faBasketShopping, faPills, faHeartPulse, faKitMedical } from '@fortawesome/free-solid-svg-icons';
import './AdminShopCategories.css';
import { fetchWithAuth } from '../../utils/authUtils';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const AdminShopCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', icon: '' });
  const [showIconDropdown, setShowIconDropdown] = useState(false);

  // Ngăn scroll body khi modal mở
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/categories`);
      const data = await response.json();
      
      if (data.success) {
        setCategories(data.data || []);
      } else {
        setError(data.message || 'Lỗi khi tải danh mục');
      }
    } catch (err) {
      setError('Không thể kết nối đến server');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCategory(null);
    setFormData({ name: '', icon: '' });
    setShowModal(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, icon: category.icon || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa danh mục này?')) return;

    try {
      const response = await fetchWithAuth(`${API_BASE}/api/categories/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      
      if (data.success) {
        alert('Xóa danh mục thành công');
        fetchCategories();
      } else {
        alert(data.message || 'Lỗi khi xóa danh mục');
      }
    } catch (err) {
      alert('Không thể kết nối đến server');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Tên danh mục là bắt buộc');
      return;
    }

    try {
      const url = editingCategory 
        ? `${API_BASE}/api/categories/${editingCategory.id}`
        : `${API_BASE}/api/categories`;
      
      const method = editingCategory ? 'PUT' : 'POST';
      
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          icon: formData.icon || null,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(editingCategory ? 'Cập nhật thành công' : 'Thêm danh mục thành công');
        setShowModal(false);
        fetchCategories();
      } else {
        alert(data.message || 'Lỗi khi lưu danh mục');
      }
    } catch (err) {
      alert('Không thể kết nối đến server');
    }
  };

  const iconOptions = [
    { value: 'fa-layer-group', label: 'Tất cả', icon: faLayerGroup },
    { value: 'fa-stethoscope', label: 'Thiết bị y tế', icon: faStethoscope },
    { value: 'fa-bowl-food', label: 'Dinh dưỡng', icon: faBowlFood },
    { value: 'fa-wheelchair', label: 'Hỗ trợ vận động', icon: faWheelchair },
    { value: 'fa-basket-shopping', label: 'Tiêu dùng', icon: faBasketShopping },
    { value: 'fa-pills', label: 'Thuốc', icon: faPills },
    { value: 'fa-heart-pulse', label: 'Sức khỏe', icon: faHeartPulse },
    { value: 'fa-kit-medical', label: 'Y tế', icon: faKitMedical },
  ];

  const getIconByName = (iconName) => {
    const found = iconOptions.find(opt => opt.value === iconName);
    return found ? found.icon : null;
  };

  return (
    <div className="admin-shop-categories">
      <div className="page-header">
        <h2><FontAwesomeIcon icon={faLayerGroup} /> Quản lý danh mục sản phẩm</h2>
        <button className="btn-add" onClick={handleAdd}>
          <FontAwesomeIcon icon={faPlus} /> Thêm danh mục
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Đang tải danh mục...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchCategories} className="btn-retry">Thử lại</button>
        </div>
      ) : categories.length === 0 ? (
        <div className="empty-state">
          <p>Chưa có danh mục nào</p>
          <button className="btn-add" onClick={handleAdd}>
            <FontAwesomeIcon icon={faPlus} /> Thêm danh mục đầu tiên
          </button>
        </div>
      ) : (
        <div className="categories-table">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Icon</th>
                <th>Tên danh mục</th>
                <th>Ngày tạo</th>
                <th>Hành động</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.id}>
                  <td>{category.id}</td>
                  <td>
                    {category.icon && getIconByName(category.icon) && (
                      <FontAwesomeIcon icon={getIconByName(category.icon)} className="category-icon" />
                    )}
                  </td>
                  <td>{category.name}</td>
                  <td>{new Date(category.created_at).toLocaleDateString('vi-VN')}</td>
                  <td>
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(category)}
                      title="Sửa"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(category.id)}
                      title="Xóa"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCategory ? 'Sửa danh mục' : 'Thêm danh mục mới'}</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Tên danh mục *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nhập tên danh mục"
                  required
                />
              </div>
              <div className="form-group">
                <label>Icon (FontAwesome)</label>
                <div className="custom-icon-dropdown">
                  <button
                    type="button"
                    className="icon-dropdown-btn"
                    onClick={() => setShowIconDropdown(!showIconDropdown)}
                  >
                    {formData.icon ? (
                      <>
                        <FontAwesomeIcon icon={iconOptions.find(opt => opt.value === formData.icon)?.icon || faLayerGroup} />
                        <span>{iconOptions.find(opt => opt.value === formData.icon)?.label || 'Chọn icon'}</span>
                      </>
                    ) : (
                      <span>-- Chọn icon --</span>
                    )}
                    <FontAwesomeIcon icon={showIconDropdown ? faTimes : faPlus} />
                  </button>
                  {showIconDropdown && (
                    <div className="icon-dropdown-list">
                      {iconOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`icon-dropdown-item ${formData.icon === opt.value ? 'active' : ''}`}
                          onClick={() => {
                            setFormData({ ...formData, icon: opt.value });
                            setShowIconDropdown(false);
                          }}
                        >
                          <FontAwesomeIcon icon={opt.icon} />
                          <span>{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  <FontAwesomeIcon icon={faTimes} /> Hủy
                </button>
                <button type="submit" className="btn-save">
                  <FontAwesomeIcon icon={faSave} /> Lưu
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShopCategories;
